import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { statsApi, ticketsApi, techniciansApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  Ticket, 
  Users, 
  Phone, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  TrendingUp,
  Bot,
  Plus,
  ArrowRight,
  Activity
} from 'lucide-react';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [recentTickets, setRecentTickets] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, ticketsRes, techRes] = await Promise.all([
          statsApi.getDashboard(),
          ticketsApi.getAll({ limit: 5 }),
          techniciansApi.getAll()
        ]);
        setStats(statsRes.data);
        const ticketsData = ticketsRes.data;
        setRecentTickets(ticketsData.tickets || ticketsData || []);
        setTechnicians(techRes.data);
      } catch (error) {
        console.error('Dashboard fetch error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getPriorityClass = (priority) => {
    const classes = {
      urgent: 'priority-urgent',
      high: 'priority-high',
      medium: 'priority-medium',
      low: 'priority-low'
    };
    return classes[priority] || 'priority-medium';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <Clock className="w-4 h-4 text-[#002FA7]" />;
      case 'in_progress': return <Activity className="w-4 h-4 text-[#D97706]" />;
      case 'resolved': return <CheckCircle className="w-4 h-4 text-[#16A34A]" />;
      default: return <Clock className="w-4 h-4 text-[#71717A]" />;
    }
  };

  const getTechStatusColor = (status) => {
    switch (status) {
      case 'available': return 'bg-[#16A34A]';
      case 'busy': return 'bg-[#D97706]';
      case 'offline': return 'bg-[#71717A]';
      default: return 'bg-[#71717A]';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#002FA7]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Tickets */}
        <Card className="border border-[#E4E4E7] shadow-none hover:-translate-y-1 hover:shadow-sm transition-all animate-fade-up" data-testid="stat-total-tickets">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#71717A]">Ticket Totali</p>
                <p className="text-3xl font-medium mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {stats?.tickets?.total || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-md bg-[#002FA7]/10 flex items-center justify-center">
                <Ticket className="w-6 h-6 text-[#002FA7]" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Open Tickets */}
        <Card className="border border-[#E4E4E7] shadow-none hover:-translate-y-1 hover:shadow-sm transition-all animate-fade-up animate-delay-100" data-testid="stat-open-tickets">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#71717A]">Ticket Aperti</p>
                <p className="text-3xl font-medium mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {stats?.tickets?.open || 0}
                </p>
                {stats?.tickets?.urgent > 0 && (
                  <p className="text-xs text-[#DC2626] mt-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {stats.tickets.urgent} urgenti
                  </p>
                )}
              </div>
              <div className="w-12 h-12 rounded-md bg-[#D97706]/10 flex items-center justify-center">
                <Clock className="w-6 h-6 text-[#D97706]" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Technicians Available */}
        <Card className="border border-[#E4E4E7] shadow-none hover:-translate-y-1 hover:shadow-sm transition-all animate-fade-up animate-delay-200" data-testid="stat-technicians">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#71717A]">Tecnici Disponibili</p>
                <p className="text-3xl font-medium mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {stats?.technicians?.available || 0}
                  <span className="text-lg text-[#71717A]">/{stats?.technicians?.total || 0}</span>
                </p>
              </div>
              <div className="w-12 h-12 rounded-md bg-[#16A34A]/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-[#16A34A]" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Calls Today */}
        <Card className="border border-[#E4E4E7] shadow-none hover:-translate-y-1 hover:shadow-sm transition-all animate-fade-up animate-delay-300" data-testid="stat-calls">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#71717A]">Chiamate Oggi</p>
                <p className="text-3xl font-medium mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {stats?.calls?.today || 0}
                </p>
              </div>
              <div className="w-12 h-12 rounded-md bg-[#FDE047]/20 flex items-center justify-center">
                <Phone className="w-6 h-6 text-[#D97706]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Tickets */}
        <Card className="lg:col-span-2 border border-[#E4E4E7] shadow-none animate-fade-up animate-delay-200" data-testid="recent-tickets-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Ticket Recenti
            </CardTitle>
            <Link to="/tickets">
              <Button variant="ghost" size="sm" className="text-[#002FA7] hover:text-[#002FA7]/80" data-testid="view-all-tickets-btn">
                Vedi tutti <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[320px]">
              {recentTickets.length === 0 ? (
                <div className="p-6 text-center text-[#71717A]">
                  <Ticket className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Nessun ticket presente</p>
                  <Link to="/tickets/new">
                    <Button size="sm" className="mt-3 bg-[#002FA7]" data-testid="create-first-ticket-btn">
                      <Plus className="w-4 h-4 mr-1" /> Crea Ticket
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="divide-y divide-[#E4E4E7]">
                  {recentTickets.map((ticket) => (
                    <Link 
                      key={ticket.id} 
                      to={`/tickets/${ticket.id}`}
                      className="block hover:bg-[#F4F4F5] transition-colors"
                      data-testid={`ticket-row-${ticket.id}`}
                    >
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {getStatusIcon(ticket.status)}
                              <span className="font-medium text-[#0F172A] truncate">
                                {ticket.title}
                              </span>
                            </div>
                            <p className="text-sm text-[#71717A] truncate">
                              {ticket.description.substring(0, 80)}...
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge className={`text-xs ${getPriorityClass(ticket.priority)} border`}>
                                {ticket.priority}
                              </Badge>
                              <span className="text-xs text-[#71717A]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                                #{ticket.id?.slice(-6)}
                              </span>
                            </div>
                          </div>
                          <div className="text-right text-xs text-[#71717A]">
                            {new Date(ticket.created_at).toLocaleDateString('it-IT')}
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Technicians Status */}
        <Card className="border border-[#E4E4E7] shadow-none animate-fade-up animate-delay-300" data-testid="technicians-status-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Stato Tecnici
            </CardTitle>
            <Link to="/technicians">
              <Button variant="ghost" size="sm" className="text-[#002FA7] hover:text-[#002FA7]/80" data-testid="view-all-technicians-btn">
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[320px]">
              {technicians.length === 0 ? (
                <div className="p-6 text-center text-[#71717A]">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Nessun tecnico registrato</p>
                </div>
              ) : (
                <div className="divide-y divide-[#E4E4E7]">
                  {technicians.map((tech) => (
                    <div key={tech.id} className="p-4 flex items-center gap-3" data-testid={`tech-row-${tech.id}`}>
                      <div className="relative">
                        <div className="w-10 h-10 rounded-full bg-[#E4E4E7] flex items-center justify-center text-[#0F172A] font-medium">
                          {tech.name?.charAt(0)?.toUpperCase() || 'T'}
                        </div>
                        <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${getTechStatusColor(tech.status)}`}></div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#0F172A] truncate">{tech.name}</p>
                        <p className="text-xs text-[#71717A] capitalize">{tech.specialty}</p>
                      </div>
                      <div className="text-right">
                        <Badge variant="secondary" className="text-xs">
                          {tech.tickets_assigned} ticket
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* AI Agent Status */}
      <Card className="border-2 border-[#FDE047] shadow-[0_0_15px_rgba(253,224,71,0.2)] ai-active-border animate-fade-up animate-delay-400" data-testid="ai-agent-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-md bg-[#FDE047] flex items-center justify-center ai-pulse">
              <Bot className="w-8 h-8 text-[#0F172A]" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  REBECCA
                </h3>
                <span className="flex items-center gap-1 text-xs text-[#16A34A] font-medium">
                  <span className="w-2 h-2 rounded-full bg-[#16A34A] live-indicator"></span>
                  ATTIVO
                </span>
              </div>
              <p className="text-[#71717A] mt-1">
                REBECCA è pronta a ricevere chiamate, trascrivere conversazioni e assistere nella diagnosi dei problemi.
              </p>
            </div>
            <Link to="/ai-chat">
              <Button className="bg-[#FDE047] text-[#0F172A] hover:bg-[#FACC15] shadow-[0_0_15px_rgba(253,224,71,0.3)]" data-testid="ai-chat-btn">
                <Bot className="w-4 h-4 mr-2" />
                REBECCA
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
